
import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { CampingSpot, Coordinates, RoadBarrier, ChargingStation, WikiSpot } from '../../types';
import { CATEGORY_MAP } from '../../services/overpassService';
import { deriveRoadAccessClass, hasOffroadRoadAccess } from '../../services/roadAccessClassifier';
import { isSnuplassSpot } from '../../services/waypointService';
import { isAutocamperSpot, isUserCreatedSpot } from '../../utils/autocamperSpots';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../constants/mapColors';
import { markProgrammaticMove } from '../../utils/mapMoveOrigin';

/**
 * True bare hvis kartet fortsatt er montert. `_loaded` forblir true etter
 * `map.remove()`, men `_mapPane` slettes — så Leaflet's `flyTo`/`getCenter`
 * krasjer på `_leaflet_pos` av undefined hvis vi treffer et destruert kart
 * via deferred callbacks (rAF, setTimeout, fetch.then).
 */
export const isMapAlive = (map: L.Map | null | undefined): boolean => {
  if (!map) return false;
  const m = map as any;
  if (!m._loaded || !m._mapPane) return false;
  const container = map.getContainer?.();
  return !!container && container.isConnected;
};

export const ForceMapResize: React.FC = () => {
  const { t } = useTranslation();
  const map = useMap();
  useEffect(() => {
    const fix = () => {
      if (!map) return;
      const container = map.getContainer?.();
      if (!container || !container.isConnected) return;
      if (container.clientWidth < 2 || container.clientHeight < 2) return;
      try {
        map.invalidateSize({ animate: false, pan: false });
      } catch {
        // Leaflet can throw during rapid mount/unmount cycles; safe to ignore and retry on next frame.
      }
    };

    map.whenReady(() => {
      setTimeout(fix, 50);
      setTimeout(fix, 300);
      setTimeout(fix, 800);
    });

    let ro: ResizeObserver | null = null;
    const container = map.getContainer();
    if (typeof ResizeObserver !== 'undefined' && container) {
      ro = new ResizeObserver(() => requestAnimationFrame(fix));
      ro.observe(container);
    }
    window.addEventListener('resize', fix);

    return () => {
      window.removeEventListener('resize', fix);
      if (ro) ro.disconnect();
    };
  }, [map]);
  return null;
};

/**
 * MapFlyHandler lytter på eksplisitte kommandoer for å flytte kartet.
 */
export const MapFlyHandler: React.FC<{
  command: { center: Coordinates; zoom: number; bounds?: [[number, number], [number, number]]; offsetY?: number; id: number } | null
}> = ({ command }) => {
  const map = useMap();
  const lastCommandId = useRef<number>(0);

  useEffect(() => {
    if (!command || command.id === lastCommandId.current) return;

    lastCommandId.current = command.id;
    // Si fra at DETTE flyttet er appens eget, ikke brukerens. Uten dette tilbyr
    // kartet «Søk i dette området» rett etter et søketreff — se utils/mapMoveOrigin.
    markProgrammaticMove();
    map.stop();

    const options = {
      duration: 2.0,
      easeLinearity: 0.15,
      animate: true,
      noMoveStart: true
    };

    requestAnimationFrame(() => {
      if (!isMapAlive(map)) return;
      try {
        if (command.bounds && Array.isArray(command.bounds) && command.bounds.length === 2) {
          map.flyToBounds(command.bounds as L.LatLngBoundsExpression, { ...options, padding: [50, 50] });
        } else if (command.offsetY) {
          // Skyv kartsenteret sørover i pikselrom slik at målpunktet havner
          // HØYERE på skjermen (over et bunnkort). offsetY = piksler pinnen
          // skal løftes fra midten.
          const pt = map.project([command.center.latitude, command.center.longitude], command.zoom);
          pt.y += command.offsetY;
          const adjusted = map.unproject(pt, command.zoom);
          map.flyTo(adjusted, command.zoom, options);
        } else {
          map.flyTo([command.center.latitude, command.center.longitude], command.zoom, options);
        }
      } catch (e) {
        try {
          if (isMapAlive(map)) map.invalidateSize({ animate: false, pan: false });
        } catch {
          // Ignore follow-up failures during rapid remount/unmount.
        }
      }
    });
  }, [command, map]);

  return null;
};

export const FitActiveTrail: React.FC<{ geometry: Coordinates[] | undefined, includePoint?: Coordinates | null, bottomPadPx?: number }> = ({ geometry, includePoint, bottomPadPx = 60 }) => {
  const map = useMap();
  useEffect(() => {
    if (!geometry || geometry.length < 2) return;
    try {
      map.stop();
      const bounds = L.latLngBounds(geometry.map(p => [p.latitude, p.longitude]));
      if (includePoint && Number.isFinite(includePoint.latitude) && Number.isFinite(includePoint.longitude)) {
        bounds.extend([includePoint.latitude, includePoint.longitude]);
      }
      map.fitBounds(bounds, { paddingTopLeft: [60, 60], paddingBottomRight: [60, bottomPadPx], maxZoom: 16, animate: true, duration: 1.2 });
    } catch (err) { console.warn("Kunne ikke tilpasse kart til tursti:", err); }
  }, [geometry, includePoint, bottomPadPx, map]);
  return null;
};

export const createCustomMarker = (iconClass: string, color: string, isSelected: boolean, isProcessing: boolean = false, imageUrl?: string, clusterCount?: number, isVerified?: boolean, isUserContributed?: boolean, hasPhotos?: boolean, isOccupied?: boolean, isInnmark?: boolean, isOffroadAccess?: boolean, offroadBadgeLabel: string = '4x4', isFootAccess?: boolean, hasSwimmableWater?: boolean, nearHouseWarning?: boolean, isRasteplassBadge?: boolean, aiSuitable?: boolean, ratingAverage?: number, calm: boolean = true) => {
  const size = isSelected ? 42 : (clusterCount ? 36 : 32);
  const iconSize = isSelected ? 18 : 14;

  // Emerald-ring = AI-vurdert høy fricamp-sikkerhet (erstatter den gamle Google-rating-
  // gull-ringen; et smartere signal enn rå rating). Kun enkeltmarkører, ikke klynger.
  // «Menneske vinner»: aiSuitable er alt filtrert bort for verifiserte/bekreftede plasser.
  const showAiRing = !!aiSuitable && !clusterCount && !isInnmark;
  // «Ren UX» (calm): tillit = ring-styrke. Menneske-verifisert får gull tillit-ring, så nålen
  // svarer «kan jeg stole på denne?» med ETT signal (ring + gull-stjerne, samme farge) i stedet
  // for stjerne + AI-skjold + rating-pille om hverandre. Rating flyttes til kortet (se under).
  const showTrustRing = calm && !!isVerified && !clusterCount && !isInnmark;
  const borderStyle = isSelected
    ? '3px solid white'
    : isInnmark
      ? '2.5px solid #f59e0b'
      : showAiRing
        ? `2.5px solid ${BRAND}`
        : showTrustRing
          ? '2.5px solid #e0a419'
          : '2px solid rgba(255,255,255,0.6)';
  let boxShadow = isInnmark && isSelected
    ? '0 4px 12px rgba(0,0,0,0.4), 0 0 0 3px rgba(245,158,11,0.5)'
    : '0 4px 12px rgba(0,0,0,0.4)';
  if (showAiRing) boxShadow += `, 0 0 0 3px ${BRAND}, 0 0 10px 2px rgba(16,185,129,0.55)`;
  if (showTrustRing) boxShadow += ', 0 0 0 2px rgba(224,164,25,0.6)';

  let innerHtml = '';
  if (imageUrl) {
    innerHtml = `<div style="width: 100%; height: 100%; border-radius: 50%; overflow: hidden; background: white;"><img src="${imageUrl}" style="width: 100%; height: 100%; object-fit: cover;"></div>`;
  } else if (isProcessing) {
    innerHtml = `<i class="fa-solid fa-spinner fa-spin" style="font-size: ${iconSize}px;"></i>`;
  } else {
    innerHtml = `<i class="${iconClass}" style="font-size: ${iconSize}px;"></i>`;
  }

  const badgeHtml = clusterCount && clusterCount > 1
    ? `<div style="position: absolute; top: -6px; right: -6px; background: #ef4444; color: white; border-radius: 999px; min-width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 900; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;">${clusterCount}</div>`
    : '';

  const verifiedHtml = isVerified
    ? `<div style="position: absolute; top: -6px; left: -6px; background: #fbbf24; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 10px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-star"></i></div>`
    : '';

  // Emerald AI-badge (skjold) = «høy sikkerhet ifølge analysen». Egen farge/form vs.
  // menneske-verifisert gull-stjerne. Vises kun når emerald-ringen vises (og aldri
  // sammen med verifisert-stjernen, siden aiSuitable filtreres bort for verifiserte).
  // Calm: emerald-RINGEN alene bærer AI-signalet — skjold-badgen dropp (ett signal, ikke to).
  const aiSuitableHtml = showAiRing && !isVerified && !calm
    ? `<div title="Høy sikkerhet" style="position: absolute; top: -6px; left: -6px; background: ${BRAND}; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 9px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-shield-halved"></i></div>`
    : '';

  const userContributedHtml = isUserContributed && !isRasteplassBadge
    ? `<div style="position: absolute; bottom: -4px; left: -4px; background: #ec4899; color: white; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 8px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-user"></i></div>`
    : '';

  const rasteplassBadgeHtml = isRasteplassBadge
    ? `<div class="fc-marker-secondary" title="Rasteplass" style="position: absolute; bottom: -4px; left: -4px; background: #1565C0; color: white; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 7px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-mug-hot"></i></div>`
    : '';

  const occupiedBadgeHtml = isOccupied
    ? `<div style="position: absolute; bottom: -4px; right: -4px; background: #dc2626; color: white; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 7px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-user"></i></div>`
    : '';

  const photoBadgeHtml = hasPhotos && !clusterCount && !isOccupied
    ? `<div class="fc-marker-secondary" style="position: absolute; bottom: -4px; right: -4px; background: ${BRAND}; color: white; border-radius: 50%; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 7px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2); z-index: 20;"><i class="fa-solid fa-camera"></i></div>`
    : '';

  // Nede-høyre-hjørnet deles av opptatt/vann/foto. Streng presedens (opptatt > vann > foto)
  // så aldri to badges rendres oppå hverandre: opptatt er mest handlingsrettet (plassen er
  // tatt), vann er en amenity-lokke, foto minst haster. `hasPhotos`-param er alt filtrert mot
  // vann i kallstedet; her legger vi til opptatt-guarden vann manglet (visuell kollisjon).
  const waterBadgeHtml = hasSwimmableWater && !clusterCount && !isOccupied
    ? `<div class="fc-marker-secondary" title="Vann innenfor 100 m" style="position: absolute; bottom: -5px; right: -5px; background: #0ea5e9; color: white; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center; font-size: 8px; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.25); z-index: 21;"><i class="fa-solid fa-person-swimming"></i></div>`
    : '';

  const offroadBadgeHtml = isOffroadAccess
    ? `<div style="position: absolute; top: -8px; left: 50%; transform: translateX(-50%); background: #f59e0b; color: #111827; border-radius: 999px; padding: 0 5px; height: 15px; display: flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 900; line-height: 1; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.25); z-index: 19; letter-spacing: 0.02em;">${offroadBadgeLabel}</div>`
    : '';

  const footAccessBadgeHtml = isFootAccess && !isOffroadAccess
    ? `<div title={t("auto.map.maputils.title.kun_til_fots", "Kun til fots")} style="position: absolute; top: -8px; left: 50%; transform: translateX(-50%); background: #0d9488; color: white; border-radius: 999px; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 9px; line-height: 1; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.25); z-index: 19;"><i class="fa-solid fa-person-hiking"></i></div>`
    : '';

  const nearHouseWarningHtml = nearHouseWarning && !clusterCount
    ? `<div title="For nær bebyggelse (&lt;150m)" style="position: absolute; top: -8px; right: -8px; background: #f97316; color: white; border-radius: 50%; width: 17px; height: 17px; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 900; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); z-index: 22;">!</div>`
    : '';

  // Stjernevurdering som lite tall NEDERST på nålen (★ 4,2). Mørk pille, amber stjerne —
  // bevisst distinkt fra gull-verifisert-stjerne (oppe-venstre, sirkel) og emerald AI-skjold.
  // Kun enkeltmarkører. Selve nålefargen koder også kvaliteten (rød/oransje/grønn).
  // Calm: rating er det svakeste tillit-språket — flyttes helt til kortets tillit-linje.
  const ratingBadgeHtml = (typeof ratingAverage === 'number') && !clusterCount && !calm
    ? `<div title="Vurdering: ${ratingAverage.toFixed(1)} av 5" style="position: absolute; bottom: -9px; left: 50%; transform: translateX(-50%); background: rgba(17,24,39,0.92); color: #fbbf24; border-radius: 999px; padding: 0 4px; height: 14px; display: flex; align-items: center; gap: 2px; font-size: 8px; font-weight: 900; line-height: 1; border: 1.5px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); z-index: 21; white-space: nowrap;">★ ${ratingAverage.toFixed(1)}</div>`
    : '';

  return L.divIcon({
    className: 'custom-fa-marker',
    html: `<div style="position: relative; width: ${size}px; height: ${size}px; background: ${color}; border: ${borderStyle}; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; box-shadow: ${boxShadow};${isSelected ? ' transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);' : ''}">${innerHtml}</div>${imageUrl ? `<div style="position: absolute; bottom: -2px; right: -2px; background: ${color}; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; z-index: 10;"><i class="${iconClass}" style="font-size: 6px; color: white;"></i></div>` : ''}${badgeHtml}${verifiedHtml}${aiSuitableHtml}${userContributedHtml}${rasteplassBadgeHtml}${occupiedBadgeHtml}${photoBadgeHtml}${waterBadgeHtml}${offroadBadgeHtml}${footAccessBadgeHtml}${nearHouseWarningHtml}${ratingBadgeHtml}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

export const createMixedClusterIcon = (count: number, isOffroadAccess: boolean = false, offroadBadgeLabel: string = '4x4') => {
  return createCustomMarker('fa-solid fa-layer-group', '#44403c', false, false, undefined, count, false, false, false, false, false, isOffroadAccess, offroadBadgeLabel);
};

type MarkerVisual = {
  icon: string;
  color: string;
  key: string;
};

const getSpotMarkerVisual = (spot: CampingSpot): MarkerVisual => {
  const featureStrings = (Array.isArray(spot.features) ? spot.features : []).map(f => String(f).toUpperCase());
  const has = (...features: string[]) => features.some(f => featureStrings.includes(f));

  if (spot.spotType === 'forbidden_area' || has('CAMPING_FORBUDT')) return { key: 'forbidden', color: '#b91c1c', icon: 'fa-solid fa-ban' };
  if (spot.spotType === 'attraction' || has('ATTRAKSJON')) return { key: 'attraction', color: '#8b5cf6', icon: 'fa-solid fa-landmark' };
  if (spot.spotType === 'commercial_campsite' || has('KOMMERSIELL_CAMPING')) return { key: 'camping', color: '#1565C0', icon: 'fa-solid fa-caravan' };
  if (has('BOM', 'SPERRING', 'BOMMER')) return { key: 'barrier', color: '#C62828', icon: 'fa-solid fa-road-barrier' };
  if (has('KAI/BRYGGE')) return { key: 'quay', color: '#0288D1', icon: 'fa-solid fa-anchor' };
  if (has('DUMP_STATION')) return { key: 'dump', color: '#8b5cf6', icon: 'fa-solid fa-faucet-drip' };
  if (has('LEKEPLASS')) return { key: 'playground', color: '#f59e0b', icon: 'fa-solid fa-child-reaching' };
  if (has('TOALETT', 'TOALETTER')) return { key: 'toilet', color: '#2563eb', icon: 'fa-solid fa-restroom' };
  if (has('VANN', 'DRIKKEVANN')) return { key: 'water', color: '#3b82f6', icon: 'fa-solid fa-droplet' };
  if (has('BADEPLASS', 'BADEPLASSER')) return { key: 'swim', color: '#0ea5e9', icon: 'fa-solid fa-person-swimming' };
  if (has('RASTEPLASS')) return { key: 'rest', color: '#1565C0', icon: 'fa-solid fa-coffee' };
  if (has('BÅLPLASS')) return { key: 'fire', color: '#f97316', icon: 'fa-solid fa-fire' };
  if (has('PICNIC')) return { key: 'picnic', color: '#3b82f6', icon: 'fa-solid fa-utensils' };
  if (has('GAPAHUK')) return { key: 'shelter', color: BRAND, icon: 'fa-solid fa-person-shelter' };
  if (has('UTSIKT')) return { key: 'view', color: BRAND, icon: 'fa-solid fa-mountain' };
  if (has('TELTPLASS')) return { key: 'tent-site', color: '#15803d', icon: 'fa-solid fa-tent' };
  return { key: 'fricamp', color: '#2E7D32', icon: 'fa-solid fa-tent' };
};

export const createMultiSpotIcon = (spots: CampingSpot[], isSelected: boolean = false) => {
  const visuals = spots.reduce<MarkerVisual[]>((acc, spot) => {
    const visual = getSpotMarkerVisual(spot);
    if (!acc.some(v => v.key === visual.key)) acc.push(visual);
    return acc;
  }, []);
  const shown = visuals.slice(0, 5);
  const extraCount = Math.max(0, visuals.length - shown.length);
  const iconSize = isSelected ? 24 : 21;
  const width = Math.max(38, shown.length * (iconSize - 3) + 18);
  const height = isSelected ? 34 : 30;
  const countHtml = spots.length > 1
    ? `<div style="position:absolute;top:-8px;right:-7px;background:#111827;color:white;border-radius:999px;min-width:19px;height:19px;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:900;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.25);">${spots.length}</div>`
    : '';
  const extraHtml = extraCount > 0
    ? `<div style="width:${iconSize}px;height:${iconSize}px;margin-left:-3px;border-radius:999px;background:#44403c;border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;font-size:9px;font-weight:900;box-shadow:0 2px 5px rgba(0,0,0,0.25);">+${extraCount}</div>`
    : '';
  const iconsHtml = shown.map((visual, index) => (
    `<div style="width:${iconSize}px;height:${iconSize}px;margin-left:${index === 0 ? 0 : -3}px;border-radius:999px;background:${visual.color};border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;box-shadow:0 2px 5px rgba(0,0,0,0.25);"><i class="${visual.icon}" style="font-size:${isSelected ? 10 : 9}px;"></i></div>`
  )).join('');

  return L.divIcon({
    className: 'multi-spot-marker',
    html: `<div style="position:relative;width:${width}px;height:${height}px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 5px 10px rgba(0,0,0,0.35));"><div style="display:flex;align-items:center;padding:4px 7px 4px 10px;border-radius:999px;background:rgba(28,25,23,0.92);border:${isSelected ? '3px' : '2px'} solid white;">${iconsHtml}${extraHtml}</div>${countHtml}</div>`,
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
  });
};

export const createCustomIcon = (spot: CampingSpot, isSelected: boolean, isProcessing: boolean = false, clusterCount?: number) => {
  const hasAi = !!spot.aiResult;
  let rawScore = hasAi ? spot.aiResult!.score : spot.score;
  const score = isNaN(rawScore) ? 0 : Math.round(rawScore);
  const featureStrings = (Array.isArray(spot.features) ? spot.features : []).map(f => String(f).toUpperCase());
  // `sourceLevel: 4` alene er IKKE «laget av en bruker»: autocamperpladsene bærer
  // nivået som vern mot opprydningsskriptene — se utils/autocamperSpots.ts. Uten
  // unntaket fikk de både rosa `fa-user-check`-nål og bruker-badge.
  const isAutocamper = isAutocamperSpot(spot);
  const isUserContribution = isUserCreatedSpot(spot);
  const isSearchResult = featureStrings.includes("SØKERESULTAT");

  const isRasteplass = featureStrings.includes("RASTEPLASS");
  const isFirepit = featureStrings.includes("BÅLPLASS");
  const isGapahuk = featureStrings.includes("GAPAHUK");
  const isPicnic = featureStrings.includes("PICNIC");
  const isUtsikt = featureStrings.includes("UTSIKT");
  const isSnuplass = isSnuplassSpot(spot);
  const isCommercial = spot.spotType === 'commercial_campsite';
  const isForbiddenArea = spot.spotType === 'forbidden_area' || featureStrings.includes('CAMPING_FORBUDT');
  const isQuay = featureStrings.includes("KAI/BRYGGE");
  const isBarrierSpot = featureStrings.includes("BOM") || featureStrings.includes("SPERRING") || featureStrings.includes("BOMMER");

  const isDumpStation = featureStrings.includes("DUMP_STATION");
  const isPlayground = featureStrings.includes("LEKEPLASS");
  const isToilet = featureStrings.includes("TOALETT") || featureStrings.includes("TOALETTER");
  const isWater = featureStrings.includes("VANN") || featureStrings.includes("DRIKKEVANN");
  const isSwimming = featureStrings.includes("BADEPLASS") || featureStrings.includes("BADEPLASSER");
  const isFricamp = featureStrings.includes("FRICAMP");
  const isTeltplass = featureStrings.includes("TELTPLASS");
  // Rasteplass som er bekreftet som fricamp (nok overnattinger) — skal rendres som fricamp med rasteplass-badge
  const isRasteplassFricamp = (spot.spotType === 'rasteplass' || isRasteplass) && isFricamp;
  const isFricampCandidate =
    isFricamp ||
    isTeltplass ||
    spot.spotType === 'algorithmic' ||
    (!isSearchResult &&
      !isCommercial &&
      !isRasteplass &&
      !isSnuplass &&
      !isQuay &&
      !isBarrierSpot &&
      !isDumpStation &&
      !isPlayground &&
      !isToilet &&
      !isWater &&
      !isSwimming &&
      !isFirepit &&
      !isPicnic);
  const hasWaterWithin100m = typeof spot.nearestWater === 'number' && spot.nearestWater <= 100;
  const hasExplicitSwimFeature = isSwimming || featureStrings.includes('STRANDSONE');
  const spotTags = Array.isArray(spot.tags) ? spot.tags : [];
  const hasStatligFriluft = spotTags.includes('Statlig Friluftsområde');
  const hasStatskog = spotTags.includes('Statskog');
  const roadAccessClass = deriveRoadAccessClass(spot).roadAccessClass;
  const isOffroadAccess = isFricampCandidate && hasOffroadRoadAccess(spot);
  const offroadBadgeLabel = roadAccessClass === 'service_road' ? 'SRV' : '4x4';
  // Marker plasser som trolig kun er tilgjengelig til fots:
  // - 'path': eksplisitt sti (alltid badge)
  // - 'unknown': kun for ikke-Fricamp-kandidater (Fricamp-spotter er allerede vurdert for vei-tilgjengelighet
  //   av scorings-algoritmen, så manglende klassifisering der er bare missing metadata, ikke fravær av vei)
  const isFootAccess = !isSearchResult && !isCommercial && !isBarrierSpot
    && (roadAccessClass === 'path' || (roadAccessClass === 'unknown' && !isFricampCandidate));

  if (spot.spotType === 'algorithmic' && score < 3 && !isSelected && !isSearchResult) return L.divIcon({ className: 'hidden-marker', html: '' });

  let color = '#2E7D32';
  let icon = 'fa-solid fa-tent';
  let usedGenericUserIcon = false;

  // Spesialhåndtering for søkeresultater som har spesifikk kategori
  let categoryMatch = null;
  if (isSearchResult) {
    const matchedKey = Object.keys(CATEGORY_MAP).find(key => featureStrings.includes(key.toUpperCase()));
    categoryMatch = matchedKey ? CATEGORY_MAP[matchedKey] : null;
  }

  // Eksplisitte kategori-features har høyest prioritet (brukervalgt kategori > auto-tags)
  if (categoryMatch) {
    color = categoryMatch.color;
    icon = categoryMatch.icon;
  }
  else if (isForbiddenArea) { color = '#b91c1c'; icon = 'fa-solid fa-ban'; }
  else if (spot.spotType === 'attraction') { color = '#8b5cf6'; icon = 'fa-solid fa-landmark'; }
  else if (isCommercial) { color = '#1565C0'; icon = spot.commercialType === 'rv_park' ? 'fa-solid fa-caravan' : 'fa-solid fa-campground'; }
  // Autocamperplads = bobilplass: samme blå bobil-nål som kommersielle rv_park,
  // fordi de ligger på samme kartlag (layers.commercialRvParks).
  else if (isAutocamper) { color = '#1565C0'; icon = 'fa-solid fa-caravan'; }
  else if (isBarrierSpot) { color = '#C62828'; icon = 'fa-solid fa-road-barrier'; }
  else if (isQuay) { color = '#0288D1'; icon = 'fa-solid fa-anchor'; }
  else if (isDumpStation) { color = '#8b5cf6'; icon = 'fa-solid fa-faucet-drip'; }
  else if (isPlayground) { color = '#f59e0b'; icon = 'fa-solid fa-child-reaching'; }
  else if (isToilet) { color = '#2563eb'; icon = 'fa-solid fa-restroom'; }
  else if (isWater) { color = '#3b82f6'; icon = 'fa-solid fa-droplet'; }
  else if (isSwimming) { color = '#0ea5e9'; icon = 'fa-solid fa-person-swimming'; }
  else if (isRasteplassFricamp) { color = '#2E7D32'; icon = 'fa-solid fa-tent'; }
  else if (isRasteplass) { color = '#1565C0'; icon = 'fa-solid fa-coffee'; }
  // Snuplass faller gjennom til fricamp-rendering (grønt telt). Snuplass-
  // statusen er fortsatt synlig som chip i SpotCard. Pre-scan har allerede
  // verifisert at snuplassene oppfyller fricamp-kriteriene (≥150m fra hus,
  // ikke i innmark/urban — se promote-snuplass-sotra.mjs).
  else if (isFirepit) { color = '#f97316'; icon = 'fa-solid fa-fire'; }
  else if (isPicnic) { color = '#3b82f6'; icon = 'fa-solid fa-utensils'; }
  else if (isGapahuk) { color = BRAND; icon = 'fa-solid fa-person-shelter'; }
  else if (isUtsikt) { color = BRAND; icon = 'fa-solid fa-mountain'; }
  else if (isFricamp) { color = '#2E7D32'; icon = 'fa-solid fa-tent'; }
  else if (isTeltplass) { color = '#15803d'; icon = 'fa-solid fa-tent'; }
  // Auto-tags (fra enrichment) kun for generelle plasser uten spesifikk feature-kategori
  else if (hasStatligFriluft) { color = '#16a34a'; icon = 'fa-solid fa-tree'; }
  else if (hasStatskog) { color = '#15803d'; icon = 'fa-solid fa-mountain'; }
  else if (isUserContribution) {
    usedGenericUserIcon = true;
    color = '#ec4899';
    icon = 'fa-solid fa-user-check';
  }
  else {
    // Standard Fricamp-logikk basert på score (inkluderer nå VEI-ENDE)
    if (score >= 8) color = '#2E7D32';
    else if (score >= 4) color = '#f59e0b';
    else color = '#78716c';
  }

  // Stjernevurdering styrer farge for vurderte plasser (rød dårlig / oransje middels /
  // grønn god) — kvaliteten avgjør fargen, som Teslas ladere. Kun der vi har en vurdering.
  const ratingAvg = typeof spot.ratingAverage === 'number' ? spot.ratingAverage : null;
  if (ratingAvg != null) {
    color = ratingAvg < 2 ? '#dc2626' : ratingAvg < 3 ? '#f59e0b' : '#2E7D32';
  }

  // Occupied override: red tent with person badge
  const occupied = !!(spot.occupiedUntil && spot.occupiedUntil > Date.now());
  if (occupied) {
    color = '#dc2626';
    icon = 'fa-solid fa-tent';
  }

  // Vis brukerfoto kun for generiske brukerbidrag (ingen spesifikk kategori)
  const imageUrl = (isUserContribution && usedGenericUserIcon) ? spot.createdByPhoto : undefined;
  // Vis bruker-badge for kategori-matchede brukerbidrag
  const showUserBadge = isUserContribution && !usedGenericUserIcon;
  // Vis kamera-badge KUN for genuint innhold (bruker-/anmeldelsesbilder), ikke for
  // auto-funnede områdebilder (auto_image_url, Mapillary/Wikimedia opptil 300 m unna).
  // Et områdebilde betyr ikke at noen har vært på plassen — badgen skal ikke antyde det.
  const hasPhotos = !!spot.photoUrls?.length;
  const showSwimmableWaterBadge = isFricampCandidate && (hasWaterWithin100m || hasExplicitSwimFeature);
  const nearHouseWarning = (typeof spot.nearestHouse === 'number' && spot.nearestHouse < 150) || !!spot.nearestHouseWarning;
  // AI-vurdert høy fricamp-sikkerhet → emerald ring/badge. «Menneske vinner»: aldri på
  // verifiserte/bekreftede plasser (de beholder gull-stjernen). Erstatter den gamle
  // googleRating≥4-gull-ringen med et smartere signal.
  const aiSuitable = spot.aiSuitability?.suitable === true
    && spot.isVerified !== true && spot.sourceLevel !== 4 && spot.spotType !== 'confirmed';
  // Stjerne-badge på markøren: bruk beregnet ratingAverage når den finnes, ellers
  // fall tilbake på det rå googleRating — slik at markøren og detaljkortet (som
  // viser googleRating) alltid er konsistente, også for ferske plasser der den
  // bayesianske ratingAverage ennå ikke er beregnet. Fargen (over) styres bevisst
  // KUN av beregnet ratingAverage, så vi ikke omfarger hele kartet på rå Google-tall.
  const ratingAverage = ratingAvg != null
    ? ratingAvg
    : (typeof spot.googleRating === 'number' ? spot.googleRating : undefined);
  return createCustomMarker(icon, color, isSelected, isProcessing, imageUrl, clusterCount, spot.isVerified, showUserBadge, hasPhotos && !showSwimmableWaterBadge, occupied, spot.isInnmark === true, isOffroadAccess, offroadBadgeLabel, isFootAccess, showSwimmableWaterBadge, nearHouseWarning, isRasteplassFricamp, aiSuitable, ratingAverage);
};

export const junctionIcon = L.divIcon({
  className: 'junction-marker',
  html: '<div class="junction-marker-inner"><div class="junction-marker-pulse"></div></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7]
});

export const getBarrierColor = (barrier: RoadBarrier): string => {
  const method = (barrier.openingMethod || '').toLowerCase();
  const type = (barrier.type || '').toLowerCase();
  // Grønn: betalingsbom — kan passeres mot betaling
  if (type.includes('betalingsbom') || method.includes('betaling')) return '#16a34a';
  // Gul: manuell, nøkkel, automatisert, eller kjente bomtyper med åpningsmulighet
  if (method.includes('manuell') || method.includes('nøkkel') || method.includes('automatisert') || method.includes('elektrisk')
    || type.includes('grind') || type.includes('løftebom') || type.includes('svingbom')) return '#d97706';
  // Rød: sperret eller ukjent
  return '#C62828';
};

export const barrierIcon = (_barrier: RoadBarrier, zoom?: number) => {
  if (zoom !== undefined && zoom >= 17) {
    // Bom-ikon ved nært zoom: liten pille med road-barrier-tegn
    return L.divIcon({
      className: '',
      html: `<div style="display:flex;align-items:center;justify-content:center;width:22px;height:14px;background:#C62828;border-radius:3px;border:1.5px solid rgba(255,255,255,0.85);box-shadow:0 1px 4px rgba(0,0,0,0.45);color:white;"><i class="fa-solid fa-road-barrier" style="font-size:9px;"></i></div>`,
      iconSize: [22, 14],
      iconAnchor: [11, 7],
    });
  }
  // Liten rød prikk ved lavt zoom
  return L.divIcon({
    className: '',
    html: `<div style="width:8px;height:8px;background:#C62828;border-radius:50%;border:1.5px solid rgba(255,255,255,0.8);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
    iconSize: [8, 8],
    iconAnchor: [4, 4],
  });
};
export const chargerIcon = (station: ChargingStation, clusterCount?: number) => createCustomMarker('fa-solid fa-bolt', '#F9A825', false, false, undefined, clusterCount);
export const wikiIcon = (wiki: WikiSpot | null, clusterCount?: number) => createCustomMarker('fa-solid fa-landmark', '#8b5cf6', false, false, undefined, clusterCount);
